export class SpeechService {
  private synth: SpeechSynthesis;
  private voices: SpeechSynthesisVoice[] = [];
  private currentLanguage: string = 'en-US';
  private speechQueue: string[] = [];
  private isProcessingQueue: boolean = false;

  constructor() {
    this.synth = window.speechSynthesis;
    this.loadVoices();
  }

  private loadVoices(): void {
    this.voices = this.synth.getVoices();
    
    if (this.voices.length === 0) {
      this.synth.addEventListener('voiceschanged', () => {
        this.voices = this.synth.getVoices();
      });
    }
  }

  speak(text: string, options?: {
    rate?: number;
    pitch?: number;
    volume?: number;
    voice?: string;
    lang?: string;
    emotion?: string;
  }): void {
    if (!text) return;

    // Cancel any ongoing speech
    this.synth.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    
    // Set options
    utterance.rate = options?.rate || 1.0;
    utterance.pitch = options?.pitch || 1.0;
    utterance.volume = options?.volume || 1.0;
    utterance.lang = options?.lang || this.currentLanguage;

    // Adjust based on emotion
    if (options?.emotion) {
      this.applyEmotionToSpeech(utterance, options.emotion);
    }

    // Set voice if specified
    if (options?.voice) {
      const selectedVoice = this.voices.find(v => v.name === options.voice);
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
    } else {
      // Auto-select voice based on language
      const voice = this.getVoiceForLanguage(utterance.lang);
      if (voice) {
        utterance.voice = voice;
      }
    }

    this.synth.speak(utterance);
  }

  speakWithQueue(text: string, options?: any): void {
    this.speechQueue.push(text);
    if (!this.isProcessingQueue) {
      this.processQueue(options);
    }
  }

  private async processQueue(options?: any): Promise<void> {
    this.isProcessingQueue = true;

    while (this.speechQueue.length > 0) {
      const text = this.speechQueue.shift();
      if (text) {
        await this.speakAsync(text, options);
      }
    }

    this.isProcessingQueue = false;
  }

  private speakAsync(text: string, options?: any): Promise<void> {
    return new Promise((resolve) => {
      const utterance = new SpeechSynthesisUtterance(text);
      
      utterance.rate = options?.rate || 1.0;
      utterance.pitch = options?.pitch || 1.0;
      utterance.volume = options?.volume || 1.0;
      utterance.lang = options?.lang || this.currentLanguage;

      if (options?.emotion) {
        this.applyEmotionToSpeech(utterance, options.emotion);
      }

      const voice = this.getVoiceForLanguage(utterance.lang);
      if (voice) {
        utterance.voice = voice;
      }

      utterance.onend = () => resolve();
      utterance.onerror = () => resolve();

      this.synth.speak(utterance);
    });
  }

  private applyEmotionToSpeech(utterance: SpeechSynthesisUtterance, emotion: string): void {
    switch (emotion.toLowerCase()) {
      case 'happy':
        utterance.rate = 1.2;
        utterance.pitch = 1.3;
        break;
      case 'sad':
        utterance.rate = 0.8;
        utterance.pitch = 0.8;
        break;
      case 'angry':
        utterance.rate = 1.3;
        utterance.pitch = 0.9;
        utterance.volume = 1.0;
        break;
      case 'surprised':
        utterance.rate = 1.4;
        utterance.pitch = 1.5;
        break;
      case 'fear':
        utterance.rate = 1.3;
        utterance.pitch = 1.4;
        break;
      case 'neutral':
      default:
        // Keep default values
        break;
    }
  }

  private getVoiceForLanguage(lang: string): SpeechSynthesisVoice | null {
    // Try exact match first
    let voice = this.voices.find(v => v.lang === lang);
    
    // Try language code match (e.g., 'en' matches 'en-US')
    if (!voice) {
      const langCode = lang.split('-')[0];
      voice = this.voices.find(v => v.lang.startsWith(langCode));
    }

    return voice || null;
  }

  getVoicesForLanguage(lang: string): SpeechSynthesisVoice[] {
    const langCode = lang.split('-')[0];
    return this.voices.filter(v => v.lang.startsWith(langCode));
  }

  setLanguage(lang: string): void {
    this.currentLanguage = lang;
  }

  stop(): void {
    this.synth.cancel();
    this.speechQueue = [];
    this.isProcessingQueue = false;
  }

  getVoices(): SpeechSynthesisVoice[] {
    return this.voices;
  }

  isSpeaking(): boolean {
    return this.synth.speaking;
  }

  pause(): void {
    this.synth.pause();
  }

  resume(): void {
    this.synth.resume();
  }
}
